<template>
  <div id="app">
    <!-- ===== LOADER ===== -->
    <div id="sn-loader" role="status" aria-label="Chargement de l'application" v-if="showLoader">
      <div class="sn-box">
        <div class="sn-header">
          <div class="sn-icon">S</div>
          <div class="sn-title">Super<span>vizr</span></div>
        </div>
        <div class="sn-spinner">
          <div class="sn-ring"></div>
          <div class="sn-ring"></div>
          <div class="sn-ring"></div>
        </div>
        <div class="sn-progress">
          <div class="sn-bar" :style="{ width: loaderProgress + '%' }"></div>
        </div>
        <div class="sn-status">
          <span class="sn-dot"></span>
          <span class="sn-msg">{{ loaderMessage }}</span>
          <span class="sn-dot"></span>
          <span class="sn-percent">{{ Math.floor(loaderProgress) }}%</span>
          <span class="sn-dot"></span>
        </div>
        <div class="sn-tag">
          <span>Connect</span>
          <span class="sn-sep">•</span>
          <span>Control</span>
          <span class="sn-sep">•</span>
          <span>Secure</span>
        </div>
      </div>
    </div>

    <!-- ===== CONTENU PRINCIPAL ===== -->
    <div v-show="!showLoader" class="w-100 container-fluid px-2 pt-2 main-content">
      <router-view></router-view>
    </div>

    <!-- ===== FOOTER MODERNISÉ ===== -->
    <footer v-show="!showLoader && !isLoginPage" class="footer-modern">
      <div class="footer-container">
        <div class="footer-bottom">
          <div class="footer-bottom-line"></div>
          <p class="footer-bottom-text">
            Tous droits réservés. Conçu avec <i class="ti ti-heart-filled heart-icon"></i> par GTECH &copy; {{ new Date().getFullYear() }}
          </p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import {
  mapActions,
  mapGetters
} from "vuex";

export default {
  data() {
    return {
      user: null,
      showLoader: true,
      loaderProgress: 0,
      loaderMessage: 'Initialisation',
      menuData: [],
      meta: {},
      loaderInterval: null,
      isAppInitialized: false,
      isMobileMenuOpen: false,
      isMobile: window.innerWidth <= 1190,
      statusMessages: [
        { min: 0, max: 20, text: 'Initialisation' },
        { min: 20, max: 40, text: 'Chargement des modules' },
        { min: 40, max: 60, text: 'Préparation de l\'interface' },
        { min: 60, max: 80, text: 'Optimisation des ressources' },
        { min: 80, max: 95, text: 'Finalisation' }
      ]
    }
  },
  name: 'App',
  computed: {
    ...mapGetters('app', ['filteredMenuItems', 'userInfo', 'userEmail', 'isLoggedIn']),
    isLoginPage() {
      return this.$route.name === 'login';
    }
  },
  watch: {
    $route(to, from) {
      if (this.isAppInitialized) {
        this.showLoader = false;
        if (this.isMobile) {
          this.closeMobileMenu();
        }
      }

      this.meta = to.meta;
      this.configureDomaines();
      this.updateNavbarAndMenuVisibility();
    },
    isLoggedIn: {
      immediate: true,
      handler(isLoggedIn) {
        console.log('🔄 État de connexion changé dans App.vue:', isLoggedIn);
        this.updateNavbarAndMenuVisibility();

        if (isLoggedIn) {
          this.updateUserInfo();
        } else {
          this.clearUserInfo();
        }
      }
    },
    isMobile(val) {
      if (!val) {
        this.closeMobileMenu();
      }
    }
  },
  mounted() {
    console.log('🏁 App.vue monté');

    window.addEventListener('resize', this.handleResize);

    if (!this.isAppInitialized) {
      this.startLoader();
    } else {
      this.showLoader = false;
    }

    this.initializeApp();
    this.updateNavbarAndMenuVisibility();
    this.user = this.$store.state.app.user ?? {};
    this.initializeEventListeners();

    // ✅ Initialiser la partie notifications (vide)
    this.initializeNotifications();

    // ✅ Configuration de la navigation vers le profil
    this.setupProfileLink();
  },

  beforeDestroy() {
    this.cleanupEventListeners();
    window.removeEventListener('resize', this.handleResize);
    if (this.loaderInterval) {
      clearInterval(this.loaderInterval);
    }
  },

  methods: {
    ...mapActions('app', ['logout']),

    handleResize() {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 1190;

      if (wasMobile && !this.isMobile) {
        this.closeMobileMenu();
      }
    },

    startLoader() {
      this.showLoader = true;
      this.loaderProgress = 0;
      this.loaderMessage = 'Initialisation';

      this.loaderInterval = setInterval(() => {
        if (this.loaderProgress < 95) {
          const increment = Math.random() * 2 + 0.5;
          this.loaderProgress = Math.min(this.loaderProgress + increment, 95);
          this.updateLoaderMessage();
        }
      }, 150);

      setTimeout(() => {
        this.hideLoader();
      }, 200);
    },

    updateLoaderMessage() {
      const message = this.statusMessages.find(m =>
        this.loaderProgress >= m.min && this.loaderProgress < m.max
      );
      if (message) {
        this.loaderMessage = message.text;
      } else if (this.loaderProgress >= 95) {
        this.loaderMessage = 'Prêt !';
      }
    },

    hideLoader() {
      if (this.loaderInterval) {
        clearInterval(this.loaderInterval);
        this.loaderInterval = null;
      }

      this.loaderProgress = 100;
      this.loaderMessage = 'Prêt !';

      setTimeout(() => {
        this.showLoader = false;
        this.isAppInitialized = true;
        document.body.style.overflow = 'auto';
        document.body.classList.add('sn-loaded');

        this.$nextTick(() => {
          this.addMenuToggleListeners();
          this.setupMobileMenuControls();
        });
      }, 500);
    },

    initializeApp() {
      if (this.isAppInitialized) {
        console.log('📋 App déjà initialisée, skip...');
        return;
      }

      this.initializeAppLogos();
      this.initializeMenu();
      this.loadExternalScripts();
      this.setupLogoutButton();
      this.setupProfileLink();
      this.configureDomaines();
      this.setupMobileMenu();

      setTimeout(() => {
        if (!this.isAppInitialized) {
          this.hideLoader();
        }
      }, 3000);
    },

    initializeAppLogos() {
      const appLogoLink = `https://api.supervizr.net/logo/${this.$store.state.app.subDomain}.supervizr.png`;

      const appLogo = document.getElementById('appLogo');
      const appLogo1 = document.getElementById('appLogo1');
      const appLogo2 = document.getElementById('appLogo2');

      const logoStyle = {
        borderRadius: '12px',
        objectFit: 'contain',
        background: 'transparent'
      };

      [appLogo, appLogo1, appLogo2].forEach(logo => {
        if (logo) {
          logo.src = appLogoLink;
          Object.assign(logo.style, logoStyle);
        }
      });
    },

    initializeMenu() {
      console.log('📋 Initialisation du menu avec', this.filteredMenuItems?.length, 'éléments');

      if (!this.filteredMenuItems || this.filteredMenuItems.length === 0) {
        console.warn('Aucun élément de menu à afficher');
        return;
      }

      const createMenuItem = (item) => {
        let li = `<li class="menu-item ${item.active ? 'active' : ''}">`;
        li += `<a href="${item.link || 'javascript:void(0)'}" class="menu-link ${item.submenu ? 'menu-toggle' : ''}">`;
        li += `<i class="menu-icon tf-icons ti ${item.icon}"></i>`;
        li += `<div data-i18n="${item.text}">${item.text}</div>`;
        li += `</a>`;

        if (item.submenu && item.submenu.length > 0) {
          li += '<ul class="menu-sub">';
          item.submenu.forEach(subItem => {
            li += createMenuItem(subItem);
          });
          li += '</ul>';
        }

        li += '</li>';
        return li;
      };

      const generateMenu = (jsonData) => {
        let html = '<ul class="menu-inner pb-2 pb-xl-0">';
        jsonData.forEach(item => {
          html += createMenuItem(item);
        });
        html += '</ul>';
        return html;
      };

      const menuHtml = generateMenu(this.filteredMenuItems);
      const menuBlock = document.getElementById('menu-block');
      if (menuBlock) {
        menuBlock.innerHTML = menuHtml;
      }

      const createMenuItem1 = (item) => {
        let li = `<li class="menu-item ${item.active ? 'active' : ''}">`;
        li += `<a href="${item.link || 'javascript:void(0)'}" class="menu-link ${item.submenu ? 'menu-toggle' : ''}" aria-expanded="false">`;
        li += `<i class="menu-icon tf-icons ti ${item.icon}"></i>`;
        li += `<div data-i18n="${item.text}">${item.text}</div>`;

        if (item.submenu && item.submenu.length > 0) {
          li += `<i class="chevron-icon tf-icons ti-angle-right"></i>`;
        }

        li += `</a>`;

        if (item.submenu && item.submenu.length > 0) {
          li += '<ul class="menu-sub">';
          item.submenu.forEach(subItem => {
            li += createMenuItem(subItem);
          });
          li += '</ul>';
        }

        li += '</li>';
        return li;
      };

      const generateMenu1 = (jsonData) => {
        let html = '';
        jsonData.forEach(item => {
          html += createMenuItem1(item);
        });
        return html;
      };

      const menuContainer = document.getElementById('menu1');
      if (menuContainer) {
        menuContainer.innerHTML = generateMenu1(this.filteredMenuItems);
      }

      this.addMenuToggleListeners();
    },

    addMenuToggleListeners() {
      this.$nextTick(() => {
        const menuToggles = document.querySelectorAll('.menu-toggle');
        menuToggles.forEach(toggle => {
          toggle.addEventListener('click', (e) => {
            e.preventDefault();
            const parentLi = toggle.closest('.menu-item');
            parentLi.classList.toggle('active');
          });
        });
      });
    },

    setupMobileMenu() {
      this.$nextTick(() => {
        if (!document.querySelector('.menu-toggle-hamburger')) {
          const menuToggleButton = document.createElement('button');
          menuToggleButton.classList.add('menu-toggle-hamburger');
          menuToggleButton.id = 'menuToggleButton';
          menuToggleButton.setAttribute('aria-label', 'Ouvrir le menu');
          menuToggleButton.setAttribute('aria-expanded', 'false');

          const menuIcon = document.createElement('span');
          menuIcon.classList.add('hamburger-icon');
          menuIcon.innerHTML = `
            <span class="hamburger-line"></span>
            <span class="hamburger-line"></span>
            <span class="hamburger-line"></span>
          `;

          menuToggleButton.appendChild(menuIcon);

          const menuContainer1 = document.getElementById('menuContainer1');
          if (menuContainer1) {
            menuContainer1.appendChild(menuToggleButton);
          }
        }

        if (!document.querySelector('.menu-close')) {
          const menuBlock = document.getElementById('menu-block1');
          if (menuBlock) {
            const closeButton = document.createElement('button');
            closeButton.classList.add('menu-close');
            closeButton.id = 'menuCloseButton';
            closeButton.setAttribute('aria-label', 'Fermer le menu');
            closeButton.innerHTML = '✕';

            menuBlock.insertBefore(closeButton, document.getElementById('menu1'));
          }
        }

        if (!document.querySelector('.menu-overlay')) {
          const overlay = document.createElement('div');
          overlay.classList.add('menu-overlay');
          overlay.id = 'menuOverlay';
          document.body.appendChild(overlay);
        }

        this.setupMobileMenuEventListeners();
      });
    },

    setupMobileMenuEventListeners() {
      this.$nextTick(() => {
        const menuToggleButton = document.getElementById('menuToggleButton');
        if (menuToggleButton) {
          menuToggleButton.removeEventListener('click', this.toggleMobileMenu);
          menuToggleButton.addEventListener('click', this.toggleMobileMenu);
        }

        const menuCloseButton = document.getElementById('menuCloseButton');
        if (menuCloseButton) {
          menuCloseButton.removeEventListener('click', this.closeMobileMenu);
          menuCloseButton.addEventListener('click', this.closeMobileMenu);
        }

        const overlay = document.getElementById('menuOverlay');
        if (overlay) {
          overlay.removeEventListener('click', this.closeMobileMenu);
          overlay.addEventListener('click', this.closeMobileMenu);
        }

        document.removeEventListener('keydown', this.handleEscKey);
        document.addEventListener('keydown', this.handleEscKey);
      });
    },

    setupMobileMenuControls() {
      this.$nextTick(() => {
        const menuItems = document.querySelectorAll('#menu1 .menu-item');
        menuItems.forEach(item => {
          const link = item.querySelector('.menu-link');
          const submenu = item.querySelector('.menu-sub');

          if (submenu && link) {
            link.removeEventListener('click', this.handleSubmenuToggle);
            link.addEventListener('click', this.handleSubmenuToggle);
          }
        });
      });
    },

    handleSubmenuToggle(e) {
      if (!this.isMobile) return;

      e.preventDefault();
      const parentLi = e.currentTarget.closest('.menu-item');
      if (parentLi) {
        parentLi.classList.toggle('active');
        const isExpanded = parentLi.classList.contains('active');
        e.currentTarget.setAttribute('aria-expanded', isExpanded);
      }
    },

    toggleMobileMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
      const layoutMenu = document.getElementById('layout-menu1');
      const menuBlock = document.getElementById('menu-block1');
      const overlay = document.getElementById('menuOverlay');
      const toggleButton = document.getElementById('menuToggleButton');

      if (this.isMobileMenuOpen) {
        layoutMenu.classList.add('open');
        menuBlock.classList.add('menu-visible');
        overlay.classList.add('active');
        document.body.classList.add('menu-open');
        toggleButton.setAttribute('aria-expanded', 'true');
        toggleButton.classList.add('active');
      } else {
        layoutMenu.classList.remove('open');
        menuBlock.classList.remove('menu-visible');
        overlay.classList.remove('active');
        document.body.classList.remove('menu-open');
        toggleButton.setAttribute('aria-expanded', 'false');
        toggleButton.classList.remove('active');
      }
    },

    closeMobileMenu() {
      this.isMobileMenuOpen = false;
      const layoutMenu = document.getElementById('layout-menu1');
      const menuBlock = document.getElementById('menu-block1');
      const overlay = document.getElementById('menuOverlay');
      const toggleButton = document.getElementById('menuToggleButton');

      layoutMenu.classList.remove('open');
      menuBlock.classList.remove('menu-visible');
      overlay.classList.remove('active');
      document.body.classList.remove('menu-open');
      toggleButton.setAttribute('aria-expanded', 'false');
      toggleButton.classList.remove('active');
    },

    handleEscKey(e) {
      if (e.key === 'Escape' && this.isMobileMenuOpen) {
        this.closeMobileMenu();
      }
    },

    updateNavbarAndMenuVisibility() {
      const isLoginPage = this.$route.name === 'login';

      this.$nextTick(() => {
        const navbar = document.getElementById('layout-navbar');
        const menu = document.getElementById('layout-menu');

        if (this.isLoggedIn && !isLoginPage) {
          if (navbar) {
            navbar.style.display = '';
            navbar.style.visibility = 'visible';
          }
          if (menu) {
            menu.style.display = '';
            menu.style.visibility = 'visible';
          }
          console.log('👁️ Navbar et menu affichés');
        } else {
          if (navbar) {
            navbar.style.display = 'none';
            navbar.style.visibility = 'hidden';
          }
          if (menu) {
            menu.style.display = 'none';
            menu.style.visibility = 'hidden';
          }
          console.log('👁️ Navbar et menu cachés');
        }
      });
    },

    updateUserInfo() {
      this.user = this.$store.state.app.user ?? {};
      console.log('👤 Mise à jour des infos utilisateur:', this.user.email);

      this.$nextTick(() => {
        const nomElement = document.getElementById('nom');
        const emailElement = document.getElementById('email');

        if (nomElement) {
          nomElement.innerHTML = this.user.firstName && this.user.lastName
            ? `${this.user.firstName} <br> ${this.user.lastName}`
            : "Utilisateur";
        }

        if (emailElement) {
          emailElement.innerHTML = this.user.email || "";
        }

        // ✅ Mettre à jour la photo de la navbar
        this.updateNavbarPhoto();

        // ✅ Re-configurer le lien profil après mise à jour du DOM
        this.setupProfileLink();

        // ✅ Rafraîchir les notifications (vide)
        this.initializeNotifications();
      });
    },

    // ✅ Mettre à jour la photo de l'avatar dans la navbar
    // ✅ Cercle parfait + pastille "en ligne" PRÉSERVÉE
    updateNavbarPhoto() {
      console.log('🖼️ Mise à jour de la photo navbar');

      this.$nextTick(() => {
        const avatarImg = document.querySelector('.dropdown-user .avatar img');

        if (!avatarImg) {
          console.warn('⚠️ Avatar navbar non trouvé');
          return;
        }

        const photo = this.user?.user_photo
                   || this.user?.photo
                   || this.$store.state.app.userPhoto
                   || null;

        // ✅ MÊME LOGIQUE QUE AgentsView
        let imgSrc = './default_photo.png';

        if (photo && photo !== '' && photo !== 'null' && photo !== 'undefined') {
          try {
            const baseUrl = this.$store.getters['app/baseAssetUrl'];
            const fullPhotoUrl = photo.startsWith('http://') || photo.startsWith('https://')
              ? this.cleanUrl(photo)
              : this.cleanUrl(baseUrl + '/' + photo);

            imgSrc = fullPhotoUrl || './default_photo.png';
          } catch (e) {
            console.error('❌ Erreur construction URL photo:', e);
          }
        }

        // ✅ Initiales capturées AVANT (this dans onerror = img)
        const initials = (
          (this.user?.firstName?.charAt(0) || '') +
          (this.user?.lastName?.charAt(0) || '')
        ).toUpperCase() || 'U';

        // ✅ Style circulaire propre sur l'image
        avatarImg.style.borderRadius = '50%';
        avatarImg.style.objectFit = 'cover';
        avatarImg.style.width = '100%';
        avatarImg.style.height = '100%';
        avatarImg.style.display = 'block';

        // ✅ Fallback initiales — CERCLE sur le SPAN (pas le parent, pour préserver la pastille)
        avatarImg.onerror = function () {
          console.log('⚠️ Image avatar introuvable → affichage des initiales');

          // 1. Cacher l'image cassée
          this.style.display = 'none';

          const parent = this.parentElement;
          if (!parent) return;

          // 2. Récupérer la taille réelle du parent
          const size = parent.offsetWidth || 40;

          // 3. NE PAS toucher au parent (sinon ::after de avatar-online serait coupé)
          //    On insère un span qui devient LUI-MÊME le cercle
          parent.innerHTML = `
            <span style="
              display: flex;
              align-items: center;
              justify-content: center;
              width: ${size}px;
              height: ${size}px;
              border-radius: 50%;
              background: linear-gradient(135deg, #3f944a 0%, #2d7a37 100%);
              color: #ffffff;
              font-weight: 700;
              font-size: ${Math.max(12, Math.floor(size * 0.42))}px;
              line-height: 1;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              font-family: Arial, Helvetica, sans-serif;
              box-shadow: 0 2px 8px rgba(63, 148, 74, 0.25);
              overflow: hidden;
            ">${initials}</span>
          `;
        };

        // ✅ IMPORTANT : assigner src APRÈS avoir défini onerror
        avatarImg.src = imgSrc;
        avatarImg.alt = `${this.user?.firstName || ''} ${this.user?.lastName || ''}`.trim() || 'Avatar';

        console.log('✅ Avatar navbar configuré →', imgSrc);
      });
    },

    // ✅ Initialiser la partie notifications (vide) — même logique que updateNavbarPhoto
    initializeNotifications() {
      console.log('🔔 Initialisation de la partie notifications (vide)');

      this.$nextTick(() => {
        const notifList = document.querySelector('.dropdown-notifications-list .list-group');
        const badge = document.querySelector('.badge-notifications');
        const headerBadge = document.querySelector('.dropdown-notifications .dropdown-header .badge');

        if (!notifList) {
          console.warn('⚠️ Conteneur notifications non trouvé');
          return;
        }

        // ✅ 1. Vider TOUTES les fausses notifications
        notifList.innerHTML = '';

        // ✅ 2. Afficher l'état "Aucune notification"
        notifList.innerHTML = `
          <li class="list-group-item text-center py-5" id="emptyNotificationState">
            <div class="d-flex flex-column align-items-center">
              <i class="ti ti-bell-off" style="font-size: 3rem; color: #b0b8c1;"></i>
              <h6 class="mt-3 mb-1 text-muted">Aucune notification</h6>
              <small class="text-muted">Vous êtes à jour ! 🎉</small>
            </div>
          </li>
        `;

        // ✅ 3. Cacher le badge rouge (points rouges sur la cloche)
        if (badge) {
          badge.style.display = 'none';
          console.log('🔕 Badge rouge caché');
        }

        // ✅ 4. Mettre le compteur à 0
        if (headerBadge) {
          headerBadge.textContent = '0';
          console.log('🔢 Compteur mis à 0');
        }

        console.log('✅ Notifications initialisées (vide)');
      });
    },

    // ✅ Nettoyer l'URL (retirer le port pour api.supervizr.net)
    cleanUrl(url) {
      try {
        if (!url) return null;
        const parsedUrl = new URL(url);
        if (parsedUrl.hostname.includes('api.supervizr.net')) {
          parsedUrl.port = '';
        }
        return parsedUrl.toString();
      } catch (e) {
        console.error("URL invalide :", url);
        return url;
      }
    },

    clearUserInfo() {
      console.log('👤 Effacement des infos utilisateur');

      this.$nextTick(() => {
        const nomElement = document.getElementById('nom');
        const emailElement = document.getElementById('email');

        if (nomElement) nomElement.innerHTML = "Non connecté";
        if (emailElement) emailElement.innerHTML = "";

        // ✅ Réinitialiser la photo navbar
        const avatarImg = document.querySelector('.dropdown-user .avatar img');
        if (avatarImg) {
          avatarImg.src = '../assets/img/profil.png';
          console.log('🖼️ Avatar navbar réinitialisé');
        }

        // ✅ Réinitialiser les notifications (vide)
        this.initializeNotifications();
      });
    },

    // ✅ Configuration du lien Mon Profil
    setupProfileLink() {
      console.log('🔧 Configuration du lien Mon Profil');

      this.$nextTick(() => {
        const profileLink = document.querySelector('.dropdown-user a[href="pages-profile-user.html"]');

        if (!profileLink) {
          const allLinks = document.querySelectorAll('.dropdown-user .dropdown-item');
          let foundLink = null;

          allLinks.forEach(link => {
            if (link.textContent && link.textContent.trim().includes('Mon Profil')) {
              foundLink = link;
            }
          });

          if (!foundLink) {
            console.warn('⚠️ Lien Mon Profil non trouvé');
            return;
          }

          this.attachProfileClickHandler(foundLink);
          return;
        }

        this.attachProfileClickHandler(profileLink);
      });
    },

    attachProfileClickHandler(linkElement) {
      const newLink = linkElement.cloneNode(true);
      linkElement.parentNode.replaceChild(newLink, linkElement);

      newLink.setAttribute('href', 'javascript:void(0);');
      newLink.id = 'monProfilLink';

      newLink.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('👤 Navigation vers Mon Profil');

        if (this.$route.path !== '/mon-profil') {
          this.$router.push('/mon-profil').catch(err => {
            if (err.name !== 'NavigationDuplicated') {
              console.error('❌ Erreur navigation profil:', err);
            }
          });
        }
      });

      console.log('✅ Lien Mon Profil configuré');
    },

    setupLogoutButton() {
      console.log('🔧 Configuration du bouton de déconnexion');

      this.$nextTick(() => {
        let deconnectionBouton = document.getElementById("boutonDeconnection");

        if (deconnectionBouton) {
          const newButton = deconnectionBouton.cloneNode(true);
          deconnectionBouton.parentNode.replaceChild(newButton, deconnectionBouton);

          const updatedButton = document.getElementById("boutonDeconnection");

          if (updatedButton) {
            updatedButton.replaceWith(updatedButton.cloneNode(true));
            const finalButton = document.getElementById("boutonDeconnection");

            finalButton.addEventListener('click', async (e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('🚪 Déconnexion demandée');

              try {
                await this.logout();
                console.log('✅ Déconnexion réussie');

                if (this.$router.currentRoute.name !== 'login') {
                  this.$router.push('/login');
                }
              } catch (error) {
                console.error('❌ Erreur lors de la déconnexion:', error);
                this.$router.push('/login');
              }
            });
          }
        }
      });
    },

    configureDomaines() {
      console.log('🌐 Configuration des domaines pour:', this.$route.name);

      let subDomaine = this.$store.state.app.subDomain;
      let pageTitle = "";

      if (this.$route.meta && this.$route.meta.pageTitle) {
        pageTitle = this.$route.meta.pageTitle;
        subDomaine = " " + pageTitle;
      }

      this.$nextTick(() => {
        const appName = document.getElementById('appName');
        if (appName) {
          appName.textContent = subDomaine;
        }

        document.title = pageTitle || 'Supervizr';

        this.updateFavicon();
      });
    },

    updateFavicon() {
      const faviconLink = `https://api.supervizr.net/logo/${this.$store.state.app.subDomain}.supervizr.ico`;

      this.$nextTick(() => {
        let favicon = document.querySelector('link[rel="icon"]');

        if (!favicon) {
          favicon = document.createElement('link');
          favicon.rel = 'icon';
          document.head.appendChild(favicon);
        }

        favicon.href = faviconLink;
      });
    },

    loadExternalScripts() {
      const scripts = [
        "/assets/vendors/libs/jquery/jquery.js",
        "/assets/vendors/libs/popper/popper.js",
        "/assets/vendors/js/bootstrap.js",
        "/assets/vendors/libs/node-waves/node-waves.js",
        "/assets/vendors/libs/perfect-scrollbar/perfect-scrollbar.js",
        "/assets/vendors/libs/hammer/hammer.js",
        "/assets/vendors/libs/i18n/i18n.js",
        "/assets/vendors/libs/typeahead-js/typeahead.js",
        "/assets/vendors/js/menu.js",
        "/assets/vendors/libs/select2/select2.js",
        "/assets/vendors/libs/plyr/plyr.js",
        "/assets/js/main.js"
      ];

      scripts.forEach(src => {
        const script = document.createElement('script');
        script.src = src;
        script.async = false;
        document.body.appendChild(script);
      });
    },

    initializeEventListeners() {
      this.$root.$on('user-changed', this.handleUserChange);
      this.$root.$on('user-logged-out', this.handleUserLogout);
      this.$root.$on('refresh-application', this.handleRefresh);

      this.$store.subscribe((mutation, state) => {
        if (mutation.type === 'app/SET_USER' || mutation.type === 'app/CLEAR_USER') {
          this.updateNavbarAndMenuVisibility();
        }
      });
    },

    cleanupEventListeners() {
      this.$root.$off('user-changed', this.handleUserChange);
      this.$root.$off('user-logged-out', this.handleUserLogout);
      this.$root.$off('refresh-application', this.handleRefresh);
      document.removeEventListener('keydown', this.handleEscKey);

      const profileLink = document.getElementById('monProfilLink');
      if (profileLink) {
        profileLink.replaceWith(profileLink.cloneNode(true));
      }
    },

    handleUserChange(user) {
      console.log('🔄 Événement user-changed reçu dans App.vue');
      this.user = user;
      this.updateUserInfo();
      this.updateNavbarAndMenuVisibility();
    },

    handleUserLogout() {
      console.log('🚪 Événement user-logged-out reçu dans App.vue');
      this.user = null;
      this.clearUserInfo();
      this.updateNavbarAndMenuVisibility();
    },

    handleRefresh() {
      console.log('🔃 Rafraîchissement demandé');
      this.$forceUpdate();
    },

    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    openSupport() {
      console.log('Ouvrir le support');
    },

    openPrivacy() {
      console.log('Ouvrir la politique de confidentialité');
    },

    openTerms() {
      console.log('Ouvrir les CGU');
    }
  }
}
</script>

<style>
@media (min-width: 1200px) {
  .bg-menu-theme {
    box-shadow: none!important;
  }
}

/* ===== STYLES DU LOADER ===== */
#sn-loader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
  transition: opacity 0.8s ease, visibility 0.8s ease;
}

.sn-box {
  width: 100%;
  max-width: 500px;
  padding: 20px;
  text-align: center;
  animation: snFadeUp 0.8s cubic-bezier(0.22, 1, 0.36, 1);
  position: relative;
  z-index: 100000;
}

.sn-header {
  margin-bottom: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
}

.sn-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #3f944a 0%, #2d7a37 100%);
  border-radius: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 35px;
  color: white;
  font-weight: 800;
  box-shadow: 0 12px 40px rgba(63, 148, 74, 0.35);
  animation: snFloat 3s ease-in-out infinite;
  position: relative;
  overflow: hidden;
}

.sn-icon::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transform: rotate(45deg);
  animation: snShimmer 3s infinite;
}

.sn-title {
  font-size: 28px;
  font-weight: 700;
  color: #1a1a2e;
  letter-spacing: -0.5px;
}

.sn-title span {
  color: #3f944a;
}

.sn-spinner {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto 25px;
}

.sn-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 4px solid transparent;
  border-top-color: #3f944a;
  animation: snSpin 1.2s cubic-bezier(0.6, 0, 0.4, 1) infinite;
}

.sn-ring:nth-child(2) {
  width: 70%;
  height: 70%;
  top: 15%;
  left: 15%;
  border-top-color: #5cb85c;
  animation-duration: 1.8s;
  animation-direction: reverse;
}

.sn-ring:nth-child(3) {
  width: 40%;
  height: 40%;
  top: 30%;
  left: 30%;
  border-top-color: #8bc34a;
  animation-duration: 2.4s;
}

.sn-progress {
  margin-top: 30px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 50px;
  height: 6px;
  overflow: hidden;
  position: relative;
  backdrop-filter: blur(10px);
}

.sn-bar {
  height: 100%;
  width: 0%;
  background: linear-gradient(90deg, #3f944a, #5cb85c, #8bc34a);
  border-radius: 50px;
  transition: width 0.3s ease;
  position: relative;
}

.sn-bar::after {
  content: '';
  position: absolute;
  right: 0;
  top: 0;
  width: 30px;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4));
  filter: blur(5px);
}

.sn-status {
  margin-top: 20px;
  font-size: 15px;
  color: #6b7a8f;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 30px;
}

.sn-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  background: #3f944a;
  border-radius: 50%;
  animation: snBlink 1.4s ease-in-out infinite;
}

.sn-dot:nth-child(2) {
  animation-delay: 0.2s;
}

.sn-dot:nth-child(3) {
  animation-delay: 0.4s;
}

.sn-percent {
  font-weight: 700;
  color: #3f944a;
  font-size: 18px;
  min-width: 45px;
  display: inline-block;
}

.sn-msg {
  min-width: 120px;
  text-align: center;
}

.sn-tag {
  margin-top: 18px;
  font-size: 14px;
  font-weight: 500;
  color: #3f944a;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  opacity: 0.7;
  animation: snFade 1.5s ease 0.5s both;
  font-family: inherit;
}

.sn-tag span {
  display: inline-block;
  animation: snPulse 2s ease-in-out infinite;
}

.sn-tag .sn-sep {
  color: #a0b4c8;
  margin: 0 6px;
  font-weight: 300;
}

/* ===== ANIMATIONS DU LOADER ===== */
@keyframes snPulse {
  0%,
  100% {
    opacity: 0.7;
  }

  50% {
    opacity: 1;
  }
}

@keyframes snSpin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes snFloat {
  0%,
  100% {
    transform: translateY(0px);
  }

  50% {
    transform: translateY(-10px);
  }
}

@keyframes snShimmer {
  0% {
    transform: translateX(-100%) rotate(45deg);
  }

  100% {
    transform: translateX(100%) rotate(45deg);
  }
}

@keyframes snFadeUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes snBlink {
  0%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }

  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}

@keyframes snFade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* ===== STYLES EXISTANTS ===== */
#appLogo {
  width: 100%;
  height: 45px;
  border-radius: 5px !important;
  object-fit: contain !important;
}

#appLogo1 {
  width: 100%;
  height: 45px;
  border-radius: 5px !important;
  object-fit: contain !important;
}

#appLogo2 {
  width: 100%;
  height: 45px;
  border-radius: 5px !important;
  object-fit: contain !important;
}

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
}

.app-brand-link {
  height: 45px;
}

.app-brand-link img {
  border-radius: 12px !important;
}

.menu-vertical {
  display: none !important;
}

.modal-dialog .close {
  background-color: #fff;
  border-radius: 0.25rem;
  opacity: 1;
  padding: 0.3757rem;
  box-shadow: 0 0.0625rem 0.375rem 0 rgba(47, 43, 61, 0.1);
  transition: all 0.23s ease 0.1s;
  position: absolute;
  top: -15px;
  right: -15px;
  width: 35px;
  height: 35px;
  border: 0px !important;
}

.modal-xl {
  width: 90% !important;
  max-width: 100% !important;
}

.menu-inner1 .menu-toggle::after {
  margin-top: 5px;
  position: absolute;
  top: 0;
  right: 0;
  display: block;
  font-family: "tabler-icons";
  font-size: 1.375rem;
  transform: translateY(0%);
  content: "\ea61";
}

.menu-close {
  position: absolute;
  top: 12px;
  right: 15px;
  font-size: 20px;
  background: transparent;
  border: none;
  color: #2c3e50;
  cursor: pointer;
  z-index: 1001;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.menu-close:hover {
  color: #3f944a;
  background: rgba(63, 148, 74, 0.1);
  transform: rotate(90deg);
}

.menu-inner1 .menu-sub {
  display: none;
  padding-left: 5px;
  list-style: none;
}

.menu-inner1 .menu-item:hover>.menu-sub {
  display: block;
}

.menu-inner1 .menu-item .menu-toggle {
  cursor: pointer;
}

.menu-inner1 .menu-item.active .menu-toggle::after {
  transform: rotate(90deg);
}

.menu-inner1 .menu-sub .menu-item .menu-link {
  padding-left: 30px;
  font-size: 14px;
}

.menu-inner1 .menu-link i.menu-icon {
  font-size: 20px;
  color: #333;
  transition: color 0.3s;
}

.menu-inner1 .menu-link:hover i.menu-icon {
  color: #fff;
}

.menu-inner1 .menu-item .menu-link {
  padding: 10px;
  color: grey !important;
  margin: 5px auto;
}

.menu-inner1 .menu-item .menu-link:hover,
.menu-inner1 .menu-item.active .menu-link {
  color: white !important;
  background: #3f944a;
  border-radius: 5px;
}

.menu-inner1 .menu-item.active .menu-link i {
  color: white !important;
}

#layout-menu1 {
  position: fixed;
  top: 0;
  left: -300px;
  width: 300px !important;
  height: 100vh;
  background-color: #ffffff;
  transition: left 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  box-shadow: 4px 0 30px rgba(0, 0, 0, 0.15);
  overflow-y: auto;
  padding-top: 60px;
}

#layout-menu1.open {
  left: 0;
}

#menu-block1 {
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.3s ease, visibility 0s 0.3s;
}

#menu-block1.menu-visible {
  visibility: visible;
  opacity: 1;
  transition: opacity 0.3s ease, visibility 0s 0s;
}

/* ===== MENU OVERLAY ===== */
.menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.35s ease, visibility 0s 0.35s;
  backdrop-filter: blur(4px);
}

.menu-overlay.active {
  opacity: 1;
  visibility: visible;
  transition: opacity 0.35s ease, visibility 0s 0s;
}

/* ===== BOUTON HAMBURGER ===== */
.menu-toggle-hamburger {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.menu-toggle-hamburger:hover {
  background: rgba(63, 148, 74, 0.08);
}

.hamburger-icon {
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 28px;
  transition: all 0.3s ease;
}

.hamburger-line {
  display: block;
  height: 3px;
  width: 100%;
  background: #2c3e50;
  border-radius: 3px;
  transition: all 0.3s ease;
  transform-origin: center;
}

.menu-toggle-hamburger.active .hamburger-line:nth-child(1) {
  transform: translateY(8px) rotate(45deg);
}

.menu-toggle-hamburger.active .hamburger-line:nth-child(2) {
  opacity: 0;
  transform: scaleX(0);
}

.menu-toggle-hamburger.active .hamburger-line:nth-child(3) {
  transform: translateY(-8px) rotate(-45deg);
}

#menuToggleButton {
  display: none;
}

/* ===== RESPONSIVE MENU ===== */
@media (max-width: 1190px) {
  #menu-block {
    display: none;
  }

  #menuContainer1 {
    display: flex !important;
    align-items: center;
    justify-content: flex-start;
  }

  #menuToggleButton {
    display: flex !important;
  }

  #layout-menu1 {
    left: -300px;
    width: 300px !important;
  }

  .menu-inner1 .menu-item .menu-link {
    padding: 12px 20px;
    font-size: 15px;
  }

  .menu-inner1 .menu-sub .menu-item .menu-link {
    padding-left: 45px;
    font-size: 14px;
  }

  .menu-inner1 .menu-toggle::after {
    right: 20px;
    font-size: 1.2rem;
  }

  .menu-close {
    display: flex !important;
    top: 15px;
    right: 15px;
  }
}

@media (max-width: 768px) {
  #layout-menu1 {
    left: -280px;
    width: 280px !important;
  }

  .menu-inner1 .menu-item .menu-link {
    padding: 10px 16px;
    font-size: 14px;
  }

  .menu-inner1 .menu-sub .menu-item .menu-link {
    padding-left: 40px;
    font-size: 13px;
  }

  .menu-inner1 .menu-link i.menu-icon {
    font-size: 18px;
  }

  .menu-toggle-hamburger {
    padding: 8px;
  }

  .hamburger-icon {
    width: 24px;
    gap: 4px;
  }

  .hamburger-line {
    height: 2.5px;
  }

  #appLogo,
  #appLogo1,
  #appLogo2 {
    border-radius: 2px !important;
    height: 38px;
  }
}

@media (max-width: 480px) {
  #layout-menu1 {
    left: -100%;
    width: 85% !important;
    max-width: 320px;
  }

  #layout-menu1.open {
    left: 0;
  }

  .menu-inner1 .menu-item .menu-link {
    padding: 10px 14px;
    font-size: 13px;
  }

  .menu-inner1 .menu-sub .menu-item .menu-link {
    padding-left: 35px;
    font-size: 12px;
  }

  .menu-inner1 .menu-link i.menu-icon {
    font-size: 16px;
  }

  .menu-close {
    top: 10px;
    right: 10px;
    width: 36px;
    height: 36px;
    font-size: 18px;
  }

  .hamburger-icon {
    width: 20px;
    gap: 3.5px;
  }

  .hamburger-line {
    height: 2px;
  }

  .menu-toggle-hamburger {
    padding: 6px;
  }

  .menu-overlay {
    backdrop-filter: blur(2px);
  }

  #appLogo,
  #appLogo1,
  #appLogo2 {
    border-radius: 2px !important;
    height: 32px;
  }
}

@media (max-width: 360px) {
  #layout-menu1 {
    width: 90% !important;
    max-width: 280px;
  }

  .menu-inner1 .menu-item .menu-link {
    padding: 8px 12px;
    font-size: 12px;
  }

  .menu-inner1 .menu-sub .menu-item .menu-link {
    padding-left: 30px;
    font-size: 11px;
  }

  .menu-inner1 .menu-link i.menu-icon {
    font-size: 14px;
    margin-right: 8px !important;
  }

  #appLogo,
  #appLogo1,
  #appLogo2 {
    border-radius: 6px !important;
    height: 28px;
  }
}

/* Support pour le scroll du menu */
#layout-menu1::-webkit-scrollbar {
  width: 4px;
}

#layout-menu1::-webkit-scrollbar-track {
  background: transparent;
}

#layout-menu1::-webkit-scrollbar-thumb {
  background: #3f944a;
  border-radius: 4px;
}

#layout-menu1::-webkit-scrollbar-thumb:hover {
  background: #2d7a37;
}

/* ===== STYLES EXISTANTS SUITE ===== */
.text-disabled {
  color: gray;
}

.w-100.container-fluid {
  flex: 1;
}

@media (max-width: 768px) {
  .container-fluid {
    padding-left: 10px !important;
    padding-right: 10px !important;
  }

  .px-2 {
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }

  .pt-2 {
    padding-top: 0.5rem !important;
  }

  #appLogo {
    height: 38px;
  }

  .app-brand-link {
    height: 38px;
  }
}

@media (max-width: 480px) {
  #appLogo {
    height: 32px;
  }

  .app-brand-link {
    height: 32px;
  }
}

.version-badge {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 12px;
  color: #6c757d;
  margin-left: 5px;
}

body.menu-open {
  overflow: hidden;
}

/* Cache les chevrons avant chargement */
body:not(.sn-loaded) .ti-chevron-right::before,
body:not(.sn-loaded) .ti-chevron-down::before,
body:not(.sn-loaded) .ti-chevron-left::before,
body:not(.sn-loaded) .ti-chevron-up::before,
body:not(.sn-loaded) .fa-chevron-right::before,
body:not(.sn-loaded) .fa-chevron-down::before,
body:not(.sn-loaded) .fa-chevron-left::before,
body:not(.sn-loaded) .fa-chevron-up::before,
body:not(.sn-loaded) .bi-chevron-right::before,
body:not(.sn-loaded) .bi-chevron-down::before,
body:not(.sn-loaded) .bi-chevron-left::before,
body:not(.sn-loaded) .bi-chevron-up::before,
body:not(.sn-loaded) .dropdown-toggle::after,
body:not(.sn-loaded) .menu-toggle::after {
  display: none !important;
  content: '' !important;
}

/* ============================================================
   ===== AVATAR NAVBAR — CERCLE PARFAIT + PASTILLE VISIBLE =====
   ============================================================ */

/* Le PARENT garde overflow visible pour laisser la pastille dépasser */
.dropdown-user .avatar,
.dropdown-user .avatar-online {
  position: relative !important;
  width: 40px !important;
  height: 40px !important;
  border-radius: 50% !important;
  overflow: visible !important;      /* ✅ CRUCIAL — sinon pastille coupée */
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
  background: transparent !important;
  padding: 0 !important;
  margin: 0 !important;
}

/* Le CERCLE visuel est porté par l'image */
.dropdown-user .avatar img {
  width: 100% !important;
  height: 100% !important;
  border-radius: 50% !important;
  object-fit: cover !important;
  display: block !important;
  background: linear-gradient(135deg, #3f944a 0%, #2d7a37 100%) !important;
}

/* Le CERCLE visuel de secours est porté par le span (fallback initiales) */
.dropdown-user .avatar > span {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
  height: 100% !important;
  border-radius: 50% !important;
  background: linear-gradient(135deg, #3f944a 0%, #2d7a37 100%) !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  line-height: 1 !important;
  overflow: hidden !important;
  letter-spacing: 0.5px !important;
  font-family: Arial, Helvetica, sans-serif !important;
}

/* ✅ PASTILLE "EN LIGNE" — reste visible au-dessus du cercle */
.dropdown-user .avatar-online::after {
  content: '' !important;
  position: absolute !important;
  bottom: 0 !important;
  right: 0 !important;
  width: 10px !important;
  height: 10px !important;
  border-radius: 50% !important;
  background: #28a745 !important;
  border: 2px solid #ffffff !important;
  z-index: 3 !important;
  box-shadow: 0 0 0 1px rgba(40, 167, 69, 0.2) !important;
  pointer-events: none !important;
}

/* ===== RESPONSIVE AVATAR ===== */
@media (max-width: 768px) {
  .dropdown-user .avatar,
  .dropdown-user .avatar-online {
    width: 36px !important;
    height: 36px !important;
  }

  .dropdown-user .avatar-online::after {
    width: 9px !important;
    height: 9px !important;
    border-width: 2px !important;
  }
}

@media (max-width: 480px) {
  .dropdown-user .avatar,
  .dropdown-user .avatar-online {
    width: 32px !important;
    height: 32px !important;
  }

  .dropdown-user .avatar-online::after {
    width: 8px !important;
    height: 8px !important;
    border-width: 1.5px !important;
  }
}

/* ===== FOOTER MODERNISÉ ===== */
.footer-modern {
  background: #ffffff;
  color: #2c3e50;
  padding: 40px 20px 20px;
  margin-top: 40px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.05);
}

.footer-modern::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #3f944a, #5cb85c, #8bc34a, #5cb85c, #3f944a);
  background-size: 200% 100%;
  animation: gradientMove 4s linear infinite;
}

@keyframes gradientMove {
  0% {
    background-position: 0% 0%;
  }

  100% {
    background-position: 200% 0%;
  }
}

.footer-container {
  max-width: 1400px;
  margin: 0 auto;
}

.footer-bottom {
  margin-top: 10px;
}

.footer-bottom-line {
  height: 1px;
  background: linear-gradient(90deg, transparent, #e8ecf1, transparent);
  margin-bottom: 15px;
}

.footer-bottom-text {
  text-align: center;
  font-size: 13px;
  color: #8a9aa8;
  margin: 0;
  letter-spacing: 0.3px;
}

.heart-icon {
  color: #e74c3c;
  font-size: 14px;
  display: inline-block;
  animation: heartBeat 1.5s ease-in-out infinite;
}

@keyframes heartBeat {
  0%,
  100% {
    transform: scale(1);
  }

  14% {
    transform: scale(1.3);
  }

  28% {
    transform: scale(1);
  }

  42% {
    transform: scale(1.3);
  }

  70% {
    transform: scale(1);
  }
}

@media (max-width: 768px) {
  .footer-modern {
    padding: 30px 15px 15px;
    margin-top: 30px;
  }

  .footer-bottom-text {
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .footer-modern {
    padding: 20px 10px 10px;
    margin-top: 20px;
  }

  .footer-bottom-text {
    font-size: 11px;
  }

  .heart-icon {
    font-size: 12px;
  }
}
</style>